/**
 * Authentication Manager
 * Handles token validation, expiration checks, and logout operations
 */

import { membershipServices, ErrorTrackingAPI } from '@services'
import { STORAGE_KEYS } from '@utils/localStorage'
import { customToast } from '@common'
import { isSecurityThreat, handleTokenError, logger } from '@utils'

class AuthManager {
    constructor() {
        this.logoutCallbacks = new Set()
        this.navigationCallback = null
        this.refreshPromise = null // Promise for ongoing refresh to prevent concurrent refreshes
    }

    /**
     * Register a logout callback
     * @param {Function} callback - Function to call when logout occurs
     */
    onLogout(callback) {
        this.logoutCallbacks.add(callback)
        return () => this.logoutCallbacks.delete(callback)
    }

    /**
     * Register navigation callback for redirecting to root page
     * @param {Function} navigate - Navigation function (e.g., from useNavigate)
     */
    setNavigationCallback(navigate) {
        this.navigationCallback = navigate
    }

    /**
     * Trigger logout and notify all registered callbacks
     * @param {boolean} isSilent - If true, skip toast notification and navigation
     */
    logout(isSilent = false) {
        if (!isSilent) {
            logger.warn('AuthManager: Triggering logout')
        }

        // Show toast notification only if not silent
        if (!isSilent) {
            customToast.error('Your session has expired. Please log in again.', {
                duration: 3000,
                icon: <i className="bx bx-sm bx-error text-danger"></i>,
            })
        }

        // Clear tokens from localStorage
        membershipServices.clearTokens()

        // Notify all registered callbacks FIRST (update Redux state)
        this.logoutCallbacks.forEach((callback, index) => {
            try {
                callback()
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'AuthManager', 'logout', 'callback')
            }
        })

        // Navigate to root page AFTER state is updated (only if not silent)
        // if (!isSilent && this.navigationCallback) {
        //     // Use setTimeout to ensure Redux state update completes before navigation
        //     // Increased timeout to allow React to re-render components
        //     setTimeout(() => {
        //         this.navigationCallback('/')
        //     }, 200)
        // }
    }

    /**
     * User-initiated logout (no toast notification)
     * Revokes refresh token before clearing local tokens
     */
    async userLogout() {
        // Get refresh token before clearing
        const tokens = membershipServices.getTokens()

        // Revoke refresh token if available
        if (tokens.refreshToken) {
            try {
                await membershipServices.revokeRefreshToken({
                    refreshToken: tokens.refreshToken,
                })
                // Note: Even if revoke fails, we still clear local tokens
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'AuthManager', 'userLogout', 'revokeToken')
                // Continue with logout even if revoke fails
            }
        }

        // Clear tokens from localStorage
        membershipServices.clearTokens()

        // Notify all registered callbacks FIRST (update Redux state)
        this.logoutCallbacks.forEach((callback, index) => {
            try {
                callback()
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'AuthManager', 'userLogout', 'callback')
            }
        })

        // Navigate to root page AFTER state is updated
        if (this.navigationCallback && typeof this.navigationCallback === 'function') {
            setTimeout(() => {
                if (this.navigationCallback && typeof this.navigationCallback === 'function') {
                    this.navigationCallback('/')
                }
            }, 200)
        }
    }

    /**
     * Check if current token is valid (without triggering logout)
     * @returns {boolean} whether token is valid
     */
    isTokenValid() {
        try {
            const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
            const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)

            if (!accessToken || !expiresAt) {
                return false
            }

            const isExpired = new Date(expiresAt) <= new Date()
            return !isExpired
        } catch (error) {
            logger.error('Error checking token validity:', error)
            return false
        }
    }

    /**
     * Check if current token is valid and trigger logout if expired
     * @param {boolean} isSilent - If true, skip toast notification and navigation
     * @returns {boolean} whether token is valid
     */
    isTokenValidWithLogout(isSilent = false) {
        try {
            const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
            const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)

            if (!accessToken || !expiresAt) {
                return false
            }

            const isExpired = new Date(expiresAt) <= new Date()
            if (isExpired) {
                logger.warn('Token is expired')
                this.logout(isSilent)
                return false
            }

            return true
        } catch (error) {
            logger.error('Error checking token validity:', error)
            return false
        }
    }

    /**
     * Get authorization header value
     * Supports both Bearer token and X-Access-Token header
     * @param {Object} options - Options
     * @param {boolean} options.useCustomHeader - Use X-Access-Token instead of Authorization Bearer
     * @returns {Object} headers object with authorization
     */
    getAuthHeader(options = {}) {
        const { useCustomHeader = false } = options

        // Only check validity without triggering logout
        // Logout will be triggered by GraphQL errors or explicit calls
        if (!this.isTokenValid()) {
            return {}
        }

        const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
        if (!accessToken) {
            return {}
        }

        if (useCustomHeader) {
            return { 'X-Access-Token': accessToken }
        }

        return { Authorization: `Bearer ${accessToken}` }
    }

    /**
     * Check if refresh token is valid (exists and not expired)
     * Note: We can't check JWT expiration without decoding, so we just check if it exists
     * @returns {boolean} whether refresh token exists
     */
    isRefreshTokenValid() {
        try {
            const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
            return !!refreshToken
        } catch (error) {
            logger.error('Error checking refresh token validity:', error)
            return false
        }
    }

    /**
     * Check if token will expire within the specified time window and refresh if needed
     * This is called before member operations to ensure token is fresh
     * @param {number} timeWindowMs - Time window in milliseconds (default: 5 minutes)
     * @returns {Promise<boolean>} whether token is valid (either already valid or refreshed successfully)
     */
    async ensureTokenValid(timeWindowMs = 300000) {
        // 5 minutes = 300000 ms
        try {
            const tokens = membershipServices.getTokens()

            // No token, nothing to check
            if (!tokens.accessToken) {
                logger.debug('[ensureTokenValid] No access token found')
                return false
            }

            const isCurrentlyValid = this.isTokenValid()
            logger.debug('[ensureTokenValid] Token currently valid:', isCurrentlyValid)

            // Check expiration time if available
            if (tokens.expiresAt) {
                const expiresAt = new Date(tokens.expiresAt)
                const now = new Date()
                const timeUntilExpiry = expiresAt.getTime() - now.getTime()

                logger.debug('[ensureTokenValid] Time until expiry:', timeUntilExpiry, 'ms (', timeUntilExpiry / 1000, 'seconds)')
                logger.debug('[ensureTokenValid] Time window:', timeWindowMs, 'ms (', timeWindowMs / 1000, 'seconds)')

                // Token is expired
                if (timeUntilExpiry <= 0) {
                    logger.debug('[ensureTokenValid] Token is expired, attempting refresh')
                    if (tokens.hasRefreshToken) {
                        return await this.attemptTokenRefresh()
                    } else {
                        logger.debug('[ensureTokenValid] No refresh token available')
                        return false
                    }
                }

                // Token will expire within the time window, refresh it proactively
                if (timeUntilExpiry > 0 && timeUntilExpiry <= timeWindowMs) {
                    logger.debug('[ensureTokenValid] Token will expire within time window, refreshing proactively')
                    if (tokens.hasRefreshToken) {
                        return await this.attemptTokenRefresh()
                    } else {
                        logger.debug('[ensureTokenValid] No refresh token available for proactive refresh')
                        return isCurrentlyValid // Return current validity if can't refresh
                    }
                }

                // Token is valid and won't expire within the time window
                if (timeUntilExpiry > timeWindowMs) {
                    logger.debug("[ensureTokenValid] Token is valid and won't expire soon")
                    return true
                }
            }

            // If no expiresAt, check current validity
            if (isCurrentlyValid) {
                logger.debug('[ensureTokenValid] Token is valid (no expiry time available)')
                return true
            }

            // Token is invalid and no expiry time available
            logger.debug('[ensureTokenValid] Token is invalid, attempting refresh')
            if (tokens.hasRefreshToken) {
                return await this.attemptTokenRefresh()
            } else {
                logger.debug('[ensureTokenValid] No refresh token available')
                return false
            }
        } catch (error) {
            logger.error('[ensureTokenValid] Error ensuring token validity:', error)
            return false
        }
    }

    /**
     * Attempt to refresh access token using refresh token
     * Prevents concurrent refresh attempts
     * @returns {Promise<boolean>} whether refresh was successful
     */
    async attemptTokenRefresh() {
        // If refresh is already in progress, return the existing promise
        if (this.refreshPromise) {
            return this.refreshPromise
        }

        // Check if we have a refresh token
        const tokens = membershipServices.getTokens()
        if (!tokens.refreshToken || !this.isRefreshTokenValid()) {
            logger.warn('No refresh token available for refresh')
            return false
        }

        // Create refresh promise
        this.refreshPromise = (async () => {
            try {
                const result = await membershipServices.refreshAccessToken({
                    refreshToken: tokens.refreshToken,
                })

                if (!result.success) {
                    // Handle error based on errorCode
                    const errorResult = {
                        error: result.error,
                        errorCode: result.errorCode,
                    }

                    // Handle security threat specially
                    if (isSecurityThreat(result.errorCode)) {
                        handleTokenError(errorResult, {
                            navigate: this.navigationCallback,
                        })
                        return false
                    }

                    // Handle other token errors
                    if (result.errorCode) {
                        handleTokenError(errorResult, {
                            navigate: this.navigationCallback,
                        })
                        return false
                    }

                    // Generic error
                    ErrorTrackingAPI.sendError(result.error, 'AuthManager', 'attemptTokenRefresh', 'generic')
                    this.logout(true) // Silent logout
                    return false
                }

                logger.debug('Token refresh result:', result)

                // Refresh successful - save new tokens to localStorage
                if (result.success && result.accessToken) {
                    // Always save accessToken and expiresAt if they exist
                    // Only update refreshToken if a new one is provided
                    membershipServices.saveTokens(
                        result.accessToken,
                        result.refreshToken || null, // Explicitly pass null if not provided
                        result.expiresAt
                    )
                } else if (result.success) {
                    // If success but no accessToken, something is wrong
                    ErrorTrackingAPI.sendError(
                        'Token refresh succeeded but no accessToken returned',
                        'AuthManager',
                        'attemptTokenRefresh',
                        'missingToken'
                    )
                    this.logout(true)
                    return false
                }

                logger.debug('Token refreshed successfully')
                return true
            } catch (error) {
                logger.error('Token refresh error:', error)
                this.logout(true) // Silent logout
                return false
            } finally {
                // Clear refresh promise
                this.refreshPromise = null
            }
        })()

        return this.refreshPromise
    }

    /**
     * Handle token error based on errorCode
     * @param {Object} result - Error result with errorCode
     * @param {string} result.errorCode - Error code
     * @param {string} result.error - Error message
     */
    handleTokenErrorCode(result) {
        handleTokenError(result, {
            navigate: this.navigationCallback,
        })
    }

    /**
     * Classify error type for proper handling
     * @param {Object} error - GraphQL or network error
     * @returns {string} error type
     */
    classifyError(error) {
        // Check for explicit authentication errors
        if (error?.extensions?.code === 'UNAUTHENTICATED') {
            return 'AUTHENTICATION'
        }

        // Check for HTTP 401 status code
        if (error?.statusCode === 401) {
            return 'AUTHENTICATION'
        }

        // Check for network errors
        if (
            error?.message?.includes('NetworkError') ||
            error?.message?.includes('Failed to fetch') ||
            error?.message?.includes('Network request failed')
        ) {
            return 'NETWORK'
        }

        // Check for server errors (5xx)
        if (error?.statusCode >= 500) {
            return 'SERVER'
        }

        // Check for client errors (4xx) that are not auth-related
        if (error?.statusCode >= 400 && error?.statusCode < 500) {
            return 'CLIENT'
        }

        // Default to unknown error
        return 'UNKNOWN'
    }

    /**
     * Handle authentication errors with proper classification
     * @param {Object} error - GraphQL or network error
     */
    handleAuthError(error) {
        // Log error details for debugging
        logger.debug('GraphQL Error Details:', {
            code: error?.extensions?.code,
            message: error?.message,
            statusCode: error?.statusCode,
            path: error?.path,
            locations: error?.locations,
        })

        const errorType = this.classifyError(error)
        ErrorTrackingAPI.sendError(error, 'AuthManager', 'handleAuthError', errorType)

        switch (errorType) {
            case 'AUTHENTICATION':
                logger.warn('Authentication error detected, attempting token refresh')
                // Try to refresh token before logging out
                this.attemptTokenRefresh().then(refreshed => {
                    if (!refreshed) {
                        // Refresh failed, logout
                        this.logout()
                    }
                    // If refresh succeeded, the original request should be retried
                })
                break

            case 'NETWORK':
                logger.warn('Network error detected')
                customToast.warning('Network connection failed. Please check your internet connection and try again.', {
                    duration: 5000,
                    icon: <i className="bx bx-sm bx-wifi-off text-warning"></i>,
                })
                break

            case 'SERVER':
                logger.warn('Server error detected')
                customToast.error('Server is temporarily unavailable. Please try again later.', {
                    duration: 5000,
                    icon: <i className="bx bx-sm bx-server text-danger"></i>,
                })
                break

            case 'CLIENT':
                logger.warn('Client error detected')
                customToast.warning('Request failed. Please check your input and try again.', {
                    duration: 4000,
                    icon: <i className="bx bx-sm bx-error text-warning"></i>,
                })
                break

            default:
                logger.warn('Unknown error detected')
                customToast.info('Something went wrong. Please try again.', {
                    duration: 4000,
                    icon: <i className="bx bx-sm bx-question-mark text-info"></i>,
                })
        }
    }
}

// Create singleton instance
export const authManager = new AuthManager()
export default authManager
